import { useCallback, useEffect, useRef, useState } from "react";

import { calcInitialFrame, calcOptions, handleFinish, keyframeTypes } from "./AutoCarousel.utils";


const useAutoCarousel = ({playbackRate, duration}: {playbackRate: number, duration: number}) => {
    const [transform, setTransform] = useState("translateX(0)");
    const [isLoaded, setIsLoaded] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const firstAnimationRef = useRef<Animation>();
    const secondAnimationRef = useRef<Animation>(null);

    const firstElemRef = useRef<HTMLDivElement>(null);
    const secondElemRef = useRef<HTMLDivElement>(null);

    const isContainPlaybackRate = (elemRef: any) => elemRef.current && "playbackRate" in elemRef.current;

    const handlePlaybackRate = useCallback((playbackRate: number) => {
        if (firstAnimationRef.current && isContainPlaybackRate(firstAnimationRef)) {
            firstAnimationRef.current.playbackRate = playbackRate;
        }

        if (secondAnimationRef.current && isContainPlaybackRate(secondAnimationRef)) {
            secondAnimationRef.current.playbackRate = playbackRate;
        }
    }, []);

    const handleStart = useCallback(() => handlePlaybackRate(1), [handlePlaybackRate]);

    const handleStopped = useCallback(() => handlePlaybackRate(playbackRate), [handlePlaybackRate, playbackRate]);

    useEffect(() => {
        const handleBrowserTabChange = () => {
            if (document.visibilityState === "visible") {
                handleStart();
            } else {
                handleStopped();
            }
        };
        document.addEventListener("visibilitychange", handleBrowserTabChange);

        return () => document.removeEventListener("visibilitychange", handleBrowserTabChange);
    }, [handleStart, handleStopped]);

    useEffect(() => {
        if (firstElemRef.current?.animate) {
            return;
        }

        async function fetchPolyfill() {
            //@ts-ignore
            await import("web-animations-js");
            setIsLoaded(true);
        }

        fetchPolyfill();
    }, []);

    useEffect(() => {
        setTransform(`translate(-${containerRef.current?.offsetWidth}px)`);

        if (!firstElemRef.current || !firstElemRef.current.animate) {
            return;
        }

        if (!firstAnimationRef.current) {
            firstAnimationRef.current = firstElemRef.current.animate(
                calcInitialFrame(containerRef),
                calcOptions(keyframeTypes.initial, duration, firstElemRef, containerRef),
            );
            firstAnimationRef.current.onfinish = () =>
                handleFinish(secondElemRef, firstElemRef, firstAnimationRef, secondAnimationRef, duration);
        }
    }, [duration, isLoaded]);


    return {
        handleStart,
        handleStopped,
        transform,
        containerRef,
        firstElemRef,
        secondElemRef
    }
};

export default useAutoCarousel;
